import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NativeSelectOption {
  readonly value: string;
  readonly label: string;
}

export interface NativeSelectProps {
  readonly id: string;
  readonly value: string;
  readonly options: readonly NativeSelectOption[];
  readonly onChange: (value: string) => void;
  /** An empty first option that stands for "nothing chosen". */
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly 'aria-label'?: string;
  readonly 'aria-describedby'?: string;
  readonly 'aria-invalid'?: boolean;
  readonly className?: string;
}

/**
 * The Dukanti native select (RTL rule): the browser arrow is removed and a
 * chevron sits at the logical end, with room reserved for it, so a long
 * Arabic or Hebrew label never runs under it.
 */
export function NativeSelect({
  id,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  className,
  ...aria
}: NativeSelectProps): React.JSX.Element {
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'border-input bg-input-background text-foreground focus-visible:border-ring focus-visible:ring-ring/50 h-10 w-full appearance-none rounded-lg border bg-none pe-10 ps-4 text-start text-sm outline-none transition-all [-moz-appearance:none] [-webkit-appearance:none] focus-visible:ring disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...aria}
      >
        {placeholder === undefined ? null : <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2"
      />
    </div>
  );
}
